import { ed25519 } from '@noble/curves/ed25519';
import { API, type OrderEntity } from '@orderly.network/types';
import { encodeURLSafe as encodeBase64URLSafe } from '@stablelib/base64';
import { encode as encodeUTF8 } from '@stablelib/utf8';
import bs58 from 'bs58';
import { type Address, encodeAbiParameters, encodePacked, keccak256 } from 'viem';

import type { Logger } from 'mode-shared/types/logger';
import { ORDERLY_KEY_PREFIX, OFF_CHAIN_VERIFYING_CONTRACT_ADDRESS } from 'mode-shared/plugins/mode-trade/constants';
import type {
  AddKeyRequestMessage,
  AuthRequestParameters,
  CancelOrderParameters,
  OrderlyAPIResponse,
  OrderlyAPIClientParameters,
  RegisterAccountRequestMessage,
  RegistrationParameters,
  RequestInitWithBody,
  RequestParameters,
  RequestWithAuthParameters,
  SettleRequestMessage,
  TradingRequestParameters,
  WithdrawRequestMessage,
  OpenPositionAPIResponse,
  OrderbookSnapshot,
  BuildersUsersVolumesResponse,
  BuildersUsersVolumesParameters,
  BuildersLeaderboardParameters,
  BuildersLeaderboardResponse,
  UpdateAutoReferralParameters,
  BindReferralCodeParameters,
  CreateReferralCodeParameters,
  CheckReferralCodeResponse,
  ReferralCodeInfoParameters,
  SubAccountListResponse,
  AggregateHoldingsResponse,
  AggregateHolding,
  MarketPriceInfoResponse,
  MarketInfoResponse,
} from './types';
import { GetOrdersParameters, GetPositionHistoryParams } from 'mode-shared/plugins/mode-trade';
import { generateRandomString } from 'mode-shared/utils/generate_code';
import { getFromLocalStorage } from 'mode-shared/utils/localStorage';
import { MESSAGE_TYPES } from 'mode-shared/plugins/mode-trade/constants/Messages';
import ReferralCodeInfoResponse, { ReferralCodeInfo } from './types/responses/ReferralCodeInfoResponse';
import TotalVolumeParameters from './types/parameters/TotalVolumeParameters';
import { LeaderBoard } from './types/responses/BuildersLeaderboardResponse';
import UpdateUsersFeeRate from './types/parameters/UpdateUsersFeeRate';
import UsersFeeRateParameters from './types/parameters/UsersFeeRateParameters';
import UsersFeeRateResponse, { UserFeeRate } from './types/responses/UsersFeeRateResponse';

/**
 * This client acts a convenient decorator to handle proxied requests to the Orderly API via the Next.js internal API.
 */
export default class OrderlyAPIClient {
  // public static variables
  public static displayName = 'OrderlyAPIClient';
  public static launchDate = new Date('2025-04-10');
  // private variables
  private readonly _baseURL: string;
  private readonly _brokerID: string;
  private readonly _logger: Logger;

  public constructor({ baseURL, brokerID, logger }: OrderlyAPIClientParameters) {
    this._baseURL = baseURL;
    this._brokerID = brokerID;
    this._logger = logger;
  }

  /**
   * private methods
   */

  private async _request<ResponseType, BodyType = undefined>({
    init,
    path,
    next,
  }: RequestParameters<BodyType>): Promise<ResponseType> {
    const __logPrefix = `${OrderlyAPIClient.displayName}#_request`;
    const { body, ...otherInit } = init;
    const response = await fetch(`${this._baseURL}${path}`, {
      ...otherInit,
      ...(body && {
        body: JSON.stringify(body),
      }),
      ...(next && { next }),
    });

    if (!response.ok) {
      const _error = `failed to send request to "${path}": ${await response.text()}`;

      this._logger.error(`${__logPrefix}: ${_error}`);

      throw new Error(_error, { cause: response.status });
    }

    const json: OrderlyAPIResponse<ResponseType> = await response.json();

    if (!json.success) {
      this._logger.error(`${__logPrefix}: ${json.message}`);

      throw new Error(json.message);
    }

    return json.data;
  }

  /**
   * Sends an authorized request to the Orderly API.
   * @param {RequestWithAuthParameters<BodyType>} params - The account ID, an Orderly key and info about the request.
   * @returns {Promise<ResponseType>} A promise that resolves to the expected response.
   * @throws {Error} If the request failed to be sent.
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/api-authentication}
   * @private
   */
  private async _requestWithAuth<ResponseType, BodyType = undefined>({
    accountID,
    init,
    orderlyKey,
    path,
    ...requestParameters
  }: RequestWithAuthParameters<BodyType>): Promise<ResponseType> {
    const privateKey = bs58.decode(orderlyKey.substring(8)); // the key should be in the format "ed25519:<base58_encoded>"
    const timestamp = String(Date.now());
    const message = `${timestamp}${init.method}${path}${init.body ? JSON.stringify(init.body) : ''}`;
    const signature = ed25519.sign(encodeUTF8(message), privateKey);
    const _init: RequestInitWithBody<BodyType> = {
      ...init,
      headers: {
        ...init.headers,
        'Content-Type':
          init.method === 'GET' || init.method === 'DELETE' ? 'application/x-www-form-urlencoded' : 'application/json',
        'orderly-account-id': accountID,
        'orderly-key': `${ORDERLY_KEY_PREFIX}:${bs58.encode(ed25519.getPublicKey(privateKey))}`,
        'orderly-signature': encodeBase64URLSafe(signature).replace(/=/g, ''), // remove padding
        'orderly-timestamp': timestamp,
      },
    };

    return await this._request<ResponseType, BodyType>({
      ...requestParameters,
      init: _init,
      path,
    });
  }

  /**
   * Get or creates an ephemeral Orderly key that can be used to make short-lived requests. If the address is not registered,
   * the address will first be registered with the broker and then the key will be added.
   * If the key is not found in the local storage, a new key will be created and registered.
   * @param {string} address - The address to create a key for.
   * @param {EVMWalletClient} walletClient - The wallet client to be used to sign the EIP-712 request.
   * @param {boolean} forceCreate - Whether to force create a new key even if one exists.
   * @returns {Promise<string>} A promise that resolves to the Orderly private key in the format "ed25519:<base58_encoded>".
   */
  async getOrderlyKey(address: string, walletClient: any): Promise<string> {
    // Check for the key in local storage
    const localStorageKey = `orderly_mainnet_${address}`;
    const localStorageValue = getFromLocalStorage<{ accountId: string; orderlyKey: string }>(localStorageKey);
    if (localStorageValue) {
      return `${ORDERLY_KEY_PREFIX}:${localStorageValue.orderlyKey}`;
    }

    // Create a key
    return await this.addNewKey(walletClient, address);
  }

  async addNewKey(
    walletClient: any,
    address: string,
    subAccountID: string | undefined = undefined,
    scope = 'asset,read,trading'
  ): Promise<string> {
    const __logPrefix = `${OrderlyAPIClient.displayName}#addNewKey`;

    const chain = walletClient.getChain();
    const privateKey = ed25519.utils.randomPrivateKey();
    const brokers = await this.getBrokers(address);

    this._logger.debug(
      `${__logPrefix}: account "${address}" registered to brokers [${brokers.map((value) => `"${value}"`).join(',')}]`
    );

    // if the address is not registered, i.e. the account is not registered on "mode", register the address
    if (!brokers.some((brokerID) => brokerID === this._brokerID)) {
      this._logger.debug(
        `${__logPrefix}: account "${address}" is not registered, registering to broker "${this._brokerID}"`
      );

      await this._registerAccount(address, walletClient);
    }

    const timestamp = Date.now();
    const message: AddKeyRequestMessage = {
      subAccountID,
      brokerId: this._brokerID,
      chainId: chain.id,
      chainType: 'EVM',
      expiration: timestamp + 365 * 24 * 60 * 60 * 1000, // 365 days
      orderlyKey: `${ORDERLY_KEY_PREFIX}:${bs58.encode(ed25519.getPublicKey(privateKey))}`,
      scope,
      timestamp,
    };
    const { signature } = await walletClient.signTypedData({
      domain: this._onChainDomain(chain.id, OFF_CHAIN_VERIFYING_CONTRACT_ADDRESS),
      message: message as unknown as Record<string, unknown>,
      primaryType: 'AddOrderlyKey',
      types: MESSAGE_TYPES,
    });

    this._logger.debug(`${__logPrefix}: obtained off-chain eip-712 add orderly key signature for account "${address}"`);

    const publicKey = await this.addKey({
      message,
      signature,
      userAddress: address,
    });

    this._logger.debug(
      `${__logPrefix}: added key "${publicKey}" (public key) for account "${address}" with broker "${this._brokerID}"`
    );

    return `${ORDERLY_KEY_PREFIX}:${bs58.encode(privateKey)}`;
  }

  private _onChainDomain(chainID: number, verifyingContract: string): any {
    return {
      name: 'Orderly',
      version: '1',
      chainId: chainID,
      verifyingContract,
    };
  }

  private async _registerAccount(address: string, walletClient: any): Promise<string> {
    const __logPrefix = `${OrderlyAPIClient.displayName}#_registerAccount`;
    const timestamp = String(Date.now());
    const chain = walletClient.getChain();
    const nonce = await this.registrationNonce();
    const message: RegisterAccountRequestMessage = {
      brokerId: this._brokerID,
      chainId: chain.id,
      chainType: 'EVM',
      registrationNonce: nonce,
      timestamp,
    };
    const { signature } = await walletClient.signTypedData({
      domain: this._onChainDomain(chain.id, '0x0000000000000000000000000000000000000000'), // OFF_CHAIN_VERIFYING_CONTRACT_ADDRESS
      message: message as unknown as Record<string, unknown>,
      primaryType: 'Registration',
      types: MESSAGE_TYPES,
    });

    this._logger.debug(`${__logPrefix}: obtained off-chain eip-712 registration signature for account "${address}"`);

    return await this.registerAccount({
      message,
      signature,
      userAddress: address,
    });
  }

  /**
   * public methods
   */

  public static accountIDFromAddressAndBroker(address: string, brokerID: string): string {
    return keccak256(
      encodeAbiParameters(
        [{ type: 'address' }, { type: 'bytes32' }],
        [address as Address, keccak256(encodePacked(['string'], [brokerID]))]
      )
    );
  }

  public accountIDFromAddress(address: string): string {
    return OrderlyAPIClient.accountIDFromAddressAndBroker(address, this._brokerID);
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/add-orderly-key}
   */
  public async addKey(params: RegistrationParameters<AddKeyRequestMessage>): Promise<string> {
    const result = await this._request<Record<'orderly_key', string>, RegistrationParameters<AddKeyRequestMessage>>({
      init: {
        body: params,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      },
      path: '/v1/orderly_key',
    });

    return result.orderly_key;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/get-available-symbols}
   */
  public async availableSymbols(): Promise<API.Symbol[]> {
    const result = await this._request<Record<'rows', API.Symbol[]>>({
      init: {
        method: 'GET',
      },
      path: '/v1/public/info',
      next: {
        revalidate: 100,
      },
    });

    return result.rows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/get-price-info-for-all-symbols}
   */
  public async priceInfoForAllSymbols(): Promise<MarketPriceInfoResponse[]> {
    const result = await this._request<Record<'rows', MarketPriceInfoResponse[]>>({
      init: {
        method: 'GET',
      },
      path: '/v1/public/market_info/price_changes',
      next: {
        revalidate: 100,
      },
    });

    return result.rows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/get-futures}
   */
  public async futures(): Promise<MarketInfoResponse[]> {
    const result = await this._request<Record<'rows', MarketInfoResponse[]>>({
      init: {
        method: 'GET',
      },
      path: '/v1/public/futures',
      next: {
        revalidate: 30,
      },
    });

    return result.rows;
  }

  public brokerID(): string {
    return this._brokerID;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/cancel-order}
   */
  public async cancelOrder(
    { orderID, symbol }: CancelOrderParameters,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<string> {
    const result = await this._requestWithAuth<Record<'status', string>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'DELETE',
      },
      orderlyKey,
      path: `/v1/order?order_id${orderID}&symbol=${symbol}`,
    });

    return result.status;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/cancel-all-pending-orders}
   */
  public async cancelPendingOrders(symbol: string, { address, orderlyKey }: AuthRequestParameters): Promise<string> {
    const result = await this._requestWithAuth<Record<'status', string>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'DELETE',
      },
      orderlyKey,
      path: `/v1/orders?symbol=${symbol}`,
    });

    return result.status;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/create-order}
   */
  public async createOrder(order: OrderEntity, { address, orderlyKey }: AuthRequestParameters): Promise<number> {
    const result = await this._requestWithAuth<API.Order, OrderEntity>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'POST',
        body: order,
      },
      orderlyKey,
      path: '/v1/order',
    });

    return result.order_id;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-orders}
   */
  public async getOrders(
    { address, orderlyKey }: AuthRequestParameters,
    accountID?: string,
    parameters?: GetOrdersParameters
  ): Promise<(API.Order | API.AlgoOrder)[]> {
    const result = await this._requestWithAuth<API.OrderResponse>({
      accountID: accountID ? accountID : this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/orders${
        parameters
          ? `?${Object.entries(parameters)
              .map(([key, value]) => `${key}=${value}`)
              .join('&')}`
          : ''
      }`,
    });

    return result.rows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/check-if-address-is-registered}
   */
  public async getBrokers(address: string): Promise<string[]> {
    const result = await this._request<Record<'broker_id', string[]>>({
      init: {
        method: 'GET',
      },
      path: `/v1/get_broker?address=${address}&chain_type=EVM`,
      next: {
        revalidate: 15,
      },
    });

    return result.broker_id;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-current-holding}
   */
  public async holdings({ address, orderlyKey }: AuthRequestParameters): Promise<API.Holding[]> {
    const { holding } = await this._requestWithAuth<Record<'holding', API.Holding[]>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/client/holding',
    });

    return holding;
  }

  public async aggregateHoldings({ address, orderlyKey }: AuthRequestParameters): Promise<AggregateHolding[]> {
    const { rows } = await this._requestWithAuth<AggregateHoldingsResponse>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/client/aggregate/holding',
    });

    return rows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-one-position-info}
   */
  public async position(symbol: string, { address, orderlyKey }: AuthRequestParameters): Promise<API.Position> {
    return await this._requestWithAuth<API.Position>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/position/${symbol}`,
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/aggregate-positions}
   */
  public async aggregatePositions({ address, orderlyKey }: AuthRequestParameters): Promise<API.Position[]> {
    const { rows } = await this._requestWithAuth<Record<'rows', API.Position[]>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/client/aggregate/positions`,
    });
    return rows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-all-positions-info}
   */
  public async positions({ address, orderlyKey }: AuthRequestParameters): Promise<OpenPositionAPIResponse> {
    return await this._requestWithAuth<OpenPositionAPIResponse>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/positions',
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-position-history}
   */
  public async positionHistory(
    { address, orderlyKey }: AuthRequestParameters,
    accountID?: string,
    parameters?: GetPositionHistoryParams
  ): Promise<Record<'rows', API.PositionHistory[]>> {
    return await this._requestWithAuth<Record<'rows', API.PositionHistory[]>>({
      accountID: accountID ? accountID : this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/position_history${
        parameters
          ? `?${Object.entries(parameters)
              .map(([key, value]) => `${key}=${value}`)
              .join('&')}`
          : ''
      }`,
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/register-account}
   */
  public async registerAccount(params: RegistrationParameters<RegisterAccountRequestMessage>): Promise<string> {
    const result = await this._request<
      Record<'account_id', string>,
      RegistrationParameters<RegisterAccountRequestMessage>
    >({
      init: {
        body: params,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
      },
      path: '/v1/register_account',
    });

    return result.account_id;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/get-registration-nonce}
   */
  public async registrationNonce(): Promise<string> {
    const result = await this._request<Record<'registration_nonce', string>>({
      init: {
        method: 'GET',
      },
      path: '/v1/registration_nonce',
    });

    return result.registration_nonce;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-settle-pnl-nonce}
   */
  public async settleNonce({ address, orderlyKey }: AuthRequestParameters): Promise<string> {
    const result = await this._requestWithAuth<Record<'settle_nonce', string>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/settle_nonce',
    });

    return result.settle_nonce;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/request-pnl-settlement}
   */
  public async settlePNL(
    { message, signature, verifyingContract }: Omit<TradingRequestParameters<SettleRequestMessage>, 'userAddress'>,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<void> {
    await this._requestWithAuth<unknown, TradingRequestParameters<SettleRequestMessage>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        body: {
          message,
          signature,
          userAddress: address,
          verifyingContract,
        },
        method: 'POST',
      },
      orderlyKey,
      path: '/v1/settle_pnl',
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/create-withdraw-request}
   */
  public async withdraw(
    { message, signature, verifyingContract }: Omit<TradingRequestParameters<WithdrawRequestMessage>, 'userAddress'>,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<number> {
    const result = await this._requestWithAuth<
      Record<'withdraw_id', number>,
      TradingRequestParameters<WithdrawRequestMessage>
    >({
      accountID: this.accountIDFromAddress(address),
      init: {
        body: {
          message,
          signature,
          userAddress: address,
          verifyingContract,
        },
        method: 'POST',
      },
      orderlyKey,
      path: '/v1/withdraw_request',
    });

    return result.withdraw_id;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-withdrawal-nonce}
   */
  public async withdrawNonce({ address, orderlyKey }: AuthRequestParameters): Promise<string> {
    const result = await this._requestWithAuth<Record<'withdraw_nonce', string>>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/withdraw_nonce',
    });

    return result.withdraw_nonce;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/orderbook-snapshot}
   */
  public async orderbookSnapshot(
    symbol: string,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<OrderbookSnapshot> {
    return await this._requestWithAuth<OrderbookSnapshot>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/orderbook/${symbol}`,
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-builders-users-volumes}
   */
  public async getBuildersUsersVolumes(
    parameters: BuildersUsersVolumesParameters,
    { address, orderlyKey }: AuthRequestParameters,
    next: { revalidate?: number } = {}
  ): Promise<BuildersUsersVolumesResponse> {
    return await this._requestWithAuth<BuildersUsersVolumesResponse>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      next,
      orderlyKey,
      path: `/v1/volume/broker/daily?${Object.entries(parameters)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')}`,
    });
  }

  public async totalVolume(
    { endDate, startDate }: TotalVolumeParameters,
    authParam: AuthRequestParameters
  ): Promise<number> {
    const leaderboard = await this.getBuildersLeaderboard(
      {
        end_date: endDate,
        start_date: startDate,
      },
      authParam
    );

    return leaderboard.reduce((acc, item) => acc + item.perp_volume, 0);
  }

  public async Layer3Campaign(userAddress: string, { address, orderlyKey }: AuthRequestParameters) {
    // https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/public/get-campaign-user-info

    const next = {
      revalidate: 25,
    };

    const parameters = {
      campaign_id: 114,
      address: userAddress,
      broker_id: this._brokerID,
    };

    const data = await this._requestWithAuth<{ volume: number }>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      next,
      orderlyKey,
      path: `/v1/public/campaign/user?${Object.entries(parameters)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')}`,
    });

    return data.volume || 0;
  }

  public async layer3QuestUserVolumeBeforeOrderlyCampaign(address: string, authParam: AuthRequestParameters) {
    const dayWindow = 89;
    const tomorrow = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    const startDate = new Date();
    startDate.setTime(tomorrow.getTime() - dayWindow * 24 * 60 * 60 * 1000);
    const endDate = new Date('2025-06-11'); // start of the campaign on Orderly side
    // We have near-real-time data starting from 2025-06-11 00:00 UTC

    const params = {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      aggregateBy: 'ACCOUNT',
      size: 10000,
    };
    const next = {
      revalidate: 100,
    };
    const rows = (await this.getBuildersUsersVolumes(params, authParam, next)).rows;

    while (startDate >= OrderlyAPIClient.launchDate) {
      startDate.setTime(startDate.getTime() - dayWindow * 24 * 60 * 60 * 1000);
      endDate.setTime(endDate.getTime() - dayWindow * 24 * 60 * 60 * 1000);
      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        address,
        aggregateBy: 'ACCOUNT',
        size: 10000,
      };
      const usersVolume = await this.getBuildersUsersVolumes(params, authParam, next);
      rows.push(...usersVolume.rows);
      // sleep for a second to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    const volume = rows
      .filter((item) => item.address.toLowerCase() === address.toLowerCase())
      .reduce((acc, item) => acc + item.perp_volume, 0);

    return volume;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-builders-leaderboard}
   */
  public async getBuildersLeaderboard(
    parameters: BuildersLeaderboardParameters,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<LeaderBoard[]> {
    let fetchedRows = 0;
    let totalRows = Infinity;
    let page = 1;

    const allRows: LeaderBoard[] = [];

    while (fetchedRows < totalRows) {
      const result = await this._requestWithAuth<BuildersLeaderboardResponse>({
        accountID: this.accountIDFromAddress(address),
        init: {
          method: 'GET',
        },
        orderlyKey,
        path: `/v1/broker/leaderboard/daily?page=${page}&sort=descending_perp_volume&broker_id=mode&${Object.entries({
          aggregateBy: 'address',
          ...parameters,
        })
          .map(([key, value]) => `${key}=${value}`)
          .join('&')}`,
      });

      totalRows = result.meta.total;
      fetchedRows += result.rows.length;
      page += 1;

      allRows.push(...result.rows);
    }

    return allRows;
  }

  // Fee Tiers

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-default-builder-fee}
   */
  public async getDefaultBuilderFee({ address, orderlyKey }: AuthRequestParameters): Promise<UserFeeRate> {
    return await this._requestWithAuth<UserFeeRate>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/broker/fee_rate/default`,
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-user-fee-rates}
   */
  public async getUsersFeeRate(
    parameters: UsersFeeRateParameters,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<UserFeeRate[]> {
    let fetchedRows = 0;
    let totalRows = Infinity;
    let page = 1;

    const allRows: UserFeeRate[] = [];

    while (fetchedRows < totalRows) {
      const result = await this._requestWithAuth<UsersFeeRateResponse>({
        accountID: this.accountIDFromAddress(address),
        init: {
          method: 'GET',
        },
        orderlyKey,
        path: `/v1/broker/user_info?page=${page}&${Object.entries(parameters)
          .map(([key, value]) => `${key}=${value}`)
          .join('&')}`,
      });

      // fix the types of the rates
      const rows = result.rows.map((r) => ({
        ...r,
        maker_fee_rate: parseFloat(r.maker_fee_rate as unknown as string),
        taker_fee_rate: parseFloat(r.taker_fee_rate as unknown as string),
      }));

      totalRows = result.meta.total;
      fetchedRows += result.rows.length;
      page += 1;

      allRows.push(...rows);
    }

    return allRows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/update-user-fee-rate}
   */
  public async updateUsersFeeRate(body: UpdateUsersFeeRate, { address, orderlyKey }: AuthRequestParameters) {
    return await this._requestWithAuth<OrderlyAPIResponse<undefined>, UpdateUsersFeeRate>({
      accountID: this.accountIDFromAddress(address),
      init: {
        body,
        method: 'POST',
      },
      orderlyKey,
      path: '/v1/broker/fee_rate/set',
    });
  }

  // Referral

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-referral-code-info}
   */
  public async getReferralCodeInfo(
    parameters: ReferralCodeInfoParameters,
    { address, orderlyKey }: AuthRequestParameters
  ): Promise<ReferralCodeInfo[]> {
    let fetchedRows = 0;
    let totalRows = Infinity;
    let page = 1;

    const allRows: ReferralCodeInfo[] = [];

    while (fetchedRows < totalRows) {
      const result = await this._requestWithAuth<ReferralCodeInfoResponse>({
        accountID: this.accountIDFromAddress(address),
        init: {
          method: 'GET',
        },
        orderlyKey,
        path: `/v1/referral/admin_info?page=${page}&${Object.entries(parameters)
          .map(([key, value]) => `${key}=${value}`)
          .join('&')}`,
      });

      totalRows = result.meta.total;
      fetchedRows += result.rows.length;
      page += 1;

      allRows.push(...result.rows);
    }

    return allRows;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/builder-admin-get-auto-referral-info}
   */
  public async getAutoReferralInfo({ address, orderlyKey }: AuthRequestParameters): Promise<any> {
    return await this._requestWithAuth<any>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/referral/auto_referral/info',
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-referral-info}
   */
  public async getReferralInfo({ address, orderlyKey }: AuthRequestParameters): Promise<any> {
    return await this._requestWithAuth<any>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: '/v1/referral/info',
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/builder-admin-update-auto-referral}
   */
  public async updateAutoReferral(body: UpdateAutoReferralParameters, { address, orderlyKey }: AuthRequestParameters) {
    return await this._requestWithAuth<OrderlyAPIResponse<undefined>, UpdateAutoReferralParameters>({
      accountID: this.accountIDFromAddress(address),
      init: {
        body,
        method: 'POST',
      },
      orderlyKey,
      path: '/v1/referral/auto_referral/update',
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/create-referral-code}
   */
  public async createReferralCode(body: CreateReferralCodeParameters, { address, orderlyKey }: AuthRequestParameters) {
    if (!body.referral_code) {
      body.referral_code = generateRandomString(7);
    }

    return await this._requestWithAuth<OrderlyAPIResponse<undefined>, CreateReferralCodeParameters>({
      accountID: this.accountIDFromAddress(address),
      init: {
        body,
        method: 'POST',
      },
      orderlyKey,
      path: '/v1/referral/create',
    });
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/bind-referral-code}
   */
  public async bindReferralCode(body: BindReferralCodeParameters, { address, orderlyKey }: AuthRequestParameters) {
    return await this._requestWithAuth<OrderlyAPIResponse<undefined>, BindReferralCodeParameters>({
      accountID: this.accountIDFromAddress(address),
      init: {
        body,
        method: 'POST',
      },
      orderlyKey,
      path: '/v1/referral/bind',
    });
  }

  public async checkReferralCode(address: string): Promise<OrderlyAPIResponse<CheckReferralCodeResponse>> {
    return (await (
      await fetch(
        `https://api.orderly.org/v1/public/referral/check_ref_code?account_id=${this.accountIDFromAddress(address)}`
      )
    ).json()) as OrderlyAPIResponse<CheckReferralCodeResponse>;
  }

  /**
   * @see {@link https://orderly.network/docs/build-on-omnichain/evm-api/restful-api/private/get-sub-account}
   */
  public async getSubAccountList({ address, orderlyKey }: AuthRequestParameters): Promise<SubAccountListResponse> {
    const result = await this._requestWithAuth<SubAccountListResponse>({
      accountID: this.accountIDFromAddress(address),
      init: {
        method: 'GET',
      },
      orderlyKey,
      path: `/v1/client/sub_account`,
    });

    return result;
  }
}
